/**
 * Smoke Drive library helpers — search, paths, storage used, folder descendants.
 */

export const DRIVE_ITEM_TYPE = {
    FILE: 'file',
    FOLDER: 'folder',
};

export const DRIVE_SCOPE = {
    PERSONAL: 'personal',
    SHARED: 'shared',
};

export function driveScopeLabel(item) {
    return item?.scope === DRIVE_SCOPE.SHARED ? 'Shared team' : 'My files';
}

/**
 * @param {object[]} allItems
 * @returns {Map<string, object>}
 */
export function driveItemsById(allItems) {
    const map = new Map();
    (Array.isArray(allItems) ? allItems : []).forEach((item) => {
        if (item?.id) map.set(item.id, item);
    });
    return map;
}

/**
 * @param {object} item
 * @param {Map<string, object>} byId
 * @returns {string}
 */
export function driveItemPathLabel(item, byId) {
    const parts = [];
    let current = item?.parentId && byId ? byId.get(item.parentId) : null;
    let guard = 0;
    while (current && guard < 24) {
        parts.unshift(current.name || 'Folder');
        current = current.parentId ? byId.get(current.parentId) : null;
        guard += 1;
    }
    return [driveScopeLabel(item), ...parts].join(' / ');
}

/**
 * Name search across My files + Shared team (not only the open folder).
 * @param {object[]} allItems
 * @param {string} query
 * @param {{ includeTrash?: boolean }} [opts]
 */
export function searchDriveLibrary(allItems, query, opts = {}) {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return [];
    const includeTrash = Boolean(opts.includeTrash);
    return (Array.isArray(allItems) ? allItems : []).filter((item) => {
        if (!includeTrash && item.trashed) return false;
        if (includeTrash && !item.trashed) return false;
        const name = String(item.name || '').toLowerCase();
        const original = String(item.originalFileName || '').toLowerCase();
        return name.includes(q) || (original && original.includes(q));
    });
}

/**
 * Bytes in active (not trashed) files.
 * @param {object[]} allItems
 * @param {{ scope?: string, ownerUid?: string }} [opts]
 */
export function sumDriveLibraryBytes(allItems, opts = {}) {
    return (Array.isArray(allItems) ? allItems : []).reduce((sum, item) => {
        if (item?.type !== DRIVE_ITEM_TYPE.FILE || item.trashed) return sum;
        if (opts.scope && item.scope !== opts.scope) return sum;
        if (opts.ownerUid && item.scope === DRIVE_SCOPE.PERSONAL && item.ownerUid !== opts.ownerUid) {
            return sum;
        }
        return sum + (Number(item.sizeBytes) || 0);
    }, 0);
}

/**
 * Files inside a folder (recursive), not including the folder itself.
 * @param {object[]} allItems
 * @param {string} folderId
 * @returns {object[]}
 */
export function listDriveDescendantFiles(allItems, folderId) {
    if (!folderId) return [];
    const list = Array.isArray(allItems) ? allItems : [];
    const byParent = new Map();
    list.forEach((item) => {
        if (item.trashed) return;
        const parent = item.parentId || null;
        if (!byParent.has(parent)) byParent.set(parent, []);
        byParent.get(parent).push(item);
    });
    const files = [];
    const walk = (id) => {
        const kids = byParent.get(id) || [];
        kids.forEach((child) => {
            if (child.type === DRIVE_ITEM_TYPE.FOLDER) walk(child.id);
            else if (child.type === DRIVE_ITEM_TYPE.FILE) files.push(child);
        });
    };
    walk(folderId);
    return files;
}

export const DRIVE_FOLDER_SHARE_FILE_CAP = 80;
export const DRIVE_ZIP_MAX_BYTES = 400 * 1024 * 1024;
